(() => {
  const scopeSelector = [
    '.photo-scanning-home-section',
    '.photo-product-shell',
    '.photo-cart-shell',
    '.photo-cart-footer-shell',
    '.photo-faq-page',
    '.photo-contact-page',
    '.photo-policy-page',
  ].join(', ');

  const motionGroups = [
    {
      selector:
        '.photo-faq-hero__copy > *, .photo-policy-hero__copy > *, .photo-cart-heading__copy > *, .product__title, .photo-product-lede',
      variant: 'lift',
    },
    {
      selector:
        '.photo-product-visual, .photo-faq-hero__card, .photo-cart-heading__stamp',
      variant: 'media',
    },
    {
      selector:
        '.photo-service-highlights div, .photo-care-card, .photo-product-summary div, .photo-final-cta .photo-actions .photo-button',
      variant: 'small',
    },
    {
      selector:
        '.photo-section-heading > *, .photo-panel__heading > *, .photo-product-section-heading > *, .photo-contact-panel__heading > *, .photo-faq-group__heading, .photo-faq-index',
      variant: 'lift',
    },
    {
      selector:
        '.photo-price-card, .photo-resolution-card, .photo-process-list li, .photo-benefit-strip div, .photo-review, .photo-product-step, .photo-product-option-group, .photo-product-option-group--resolution input[type="radio"] + label, .photo-product-selection-summary, .photo-product-usb-pick, .photo-product-shipping-note, .photo-shipping-note',
      variant: 'card',
    },
    {
      selector:
        '.photo-panel, .photo-product-detail-panel, .photo-product-packaging__panel, .photo-product-faq details, .photo-faq-group, .photo-faq-accordion details, .photo-faq-cta__panel, .photo-policy-index, .photo-policy-card, .photo-contact-panel, .photo-contact-list li, .photo-contact-cta, .photo-cart-assurance div, .photo-cart-shell .cart-item, .photo-cart-next, .photo-cart-summary-card, .photo-cart-empty > *',
      variant: 'card',
    },
    {
      selector: '.photo-home-footer__grid > *, .photo-home-footer__bottom',
      variant: 'lift',
    },
  ];

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const faqAccordionSelector = '.photo-faq-accordion, .photo-product-faq';
  const observed = new WeakSet();
  const pendingObservationElements = [];
  let observationScheduled = false;
  let observer;

  function getObserver() {
    if (!('IntersectionObserver' in window)) return null;
    if (!observer) {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            scheduleMotionReady(entry.target);
            observer.unobserve(entry.target);
          });
        },
        {
          rootMargin: '0px 0px -12% 0px',
          threshold: 0.12,
        }
      );
    }

    return observer;
  }

  function toMilliseconds(value) {
    const trimmedValue = value.trim();
    if (!trimmedValue) return 0;
    if (trimmedValue.endsWith('ms')) return Number.parseFloat(trimmedValue) || 0;
    if (trimmedValue.endsWith('s')) return (Number.parseFloat(trimmedValue) || 0) * 1000;
    return Number.parseFloat(trimmedValue) || 0;
  }

  function getTransitionCompletionTime(element) {
    const styles = window.getComputedStyle(element);
    const durations = styles.transitionDuration.split(',').map(toMilliseconds);
    const delays = styles.transitionDelay.split(',').map(toMilliseconds);
    const itemCount = Math.max(durations.length, delays.length, 1);
    let completionTime = 0;

    for (let index = 0; index < itemCount; index += 1) {
      const duration = durations[index % durations.length] || 0;
      const delay = delays[index % delays.length] || 0;
      completionTime = Math.max(completionTime, duration + delay);
    }

    return completionTime;
  }

  function scheduleMotionReady(element) {
    const completionTime = getTransitionCompletionTime(element);

    window.setTimeout(() => {
      element.classList.add('photo-motion-ready');
      element.style.removeProperty('--photo-motion-delay');
    }, completionTime + 50);
  }

  function getSequentialMotionOrder(element) {
    if (
      element.matches(
        '.photo-process-list > li, .photo-resolution-grid--product > .photo-resolution-card, .photo-service-highlights > div, .photo-care-bento > .photo-care-card'
      )
    ) {
      const sequence = element.parentElement;
      if (!sequence) return null;

      return Array.from(sequence.children).indexOf(element);
    }

    if (element.matches('.photo-product-option-group--resolution input[type="radio"] + label')) {
      const sequence = element.closest('.photo-product-option-group--resolution');
      if (!sequence) return null;

      return Array.from(sequence.querySelectorAll('input[type="radio"] + label')).indexOf(element);
    }

    return null;
  }

  function flushPendingObservations() {
    observationScheduled = false;
    const activeObserver = getObserver();
    const elements = pendingObservationElements.splice(0);

    elements.forEach((element) => {
      if (activeObserver) {
        activeObserver.observe(element);
      } else {
        element.classList.add('is-visible');
      }
    });
  }

  function scheduleObservation(element) {
    pendingObservationElements.push(element);
    if (observationScheduled) return;

    observationScheduled = true;
    requestAnimationFrame(() => {
      requestAnimationFrame(flushPendingObservations);
    });
  }

  function addMotionElement(element, index, variant) {
    if (observed.has(element)) return;
    observed.add(element);

    const sequentialMotionOrder = getSequentialMotionOrder(element);
    const motionVariant = sequentialMotionOrder === null ? variant : 'process-step';
    const motionOrder =
      sequentialMotionOrder === null ? Math.min(index % 8, 7) : Math.max(sequentialMotionOrder, 0);

    element.classList.add('photo-motion-reveal');
    element.dataset.photoMotion = motionVariant;
    element.style.setProperty('--photo-motion-order', String(motionOrder));

    if (sequentialMotionOrder !== null) {
      element.style.setProperty('--photo-motion-delay', `${motionOrder * 140}ms`);
    }

    scheduleObservation(element);
  }

  function initializeMotion(root = document) {
    if (prefersReducedMotion.matches) {
      document.documentElement.classList.add('photo-motion-reduced');
      return;
    }

    const scopes = root.matches?.(scopeSelector)
      ? [root]
      : Array.from(root.querySelectorAll(scopeSelector));

    if (scopes.length === 0 && root === document) return;

    document.documentElement.classList.add('photo-motion-enabled');

    motionGroups.forEach((group) => {
      const groupRoot = group.root || root;
      const searchRoots = group.root ? [groupRoot] : scopes;

      searchRoots.forEach((searchRoot) => {
        Array.from(searchRoot.querySelectorAll(group.selector)).forEach((element, index) => {
          addMotionElement(element, index, group.variant);
        });
      });
    });
  }

  function initializeFaqAccordions(root = document) {
    const accordions = root.matches?.(faqAccordionSelector)
      ? [root]
      : Array.from(root.querySelectorAll(faqAccordionSelector));

    accordions.forEach((accordion) => {
      if (accordion.dataset.photoSingleOpen === 'true') return;
      accordion.dataset.photoSingleOpen = 'true';

      accordion.querySelectorAll('details').forEach((details) => {
        details.querySelector('summary')?.setAttribute('aria-expanded', details.open ? 'true' : 'false');
      });

      accordion.addEventListener('click', (event) => {
        const summary = event.target.closest?.('summary');
        if (!summary || !accordion.contains(summary)) return;

        const activeDetails = summary.parentElement;
        if (!(activeDetails instanceof HTMLDetailsElement)) return;

        event.preventDefault();

        const shouldOpen = !activeDetails.open;
        if (shouldOpen) {
          accordion.querySelectorAll('details[open]').forEach((details) => {
            if (details === activeDetails) return;
            animateFaqDetails(details, false);
          });
        }

        animateFaqDetails(activeDetails, shouldOpen);
      });
    });
  }

  function animateFaqDetails(details, shouldOpen) {
    const summary = details.querySelector('summary');
    const duration = prefersReducedMotion.matches ? 0 : 320;

    details.photoFaqAnimation?.cancel();

    if (duration === 0 || typeof details.animate !== 'function') {
      details.open = shouldOpen;
      summary?.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
      return;
    }

    const startHeight = details.offsetHeight;
    details.style.overflow = 'hidden';

    if (shouldOpen) {
      details.open = true;
      summary?.setAttribute('aria-expanded', 'true');
    } else {
      summary?.setAttribute('aria-expanded', 'false');
    }

    const endHeight = shouldOpen ? details.scrollHeight : summary?.offsetHeight || 0;

    details.photoFaqAnimation = details.animate(
      {
        height: [`${startHeight}px`, `${endHeight}px`],
        opacity: shouldOpen ? [0.88, 1] : [1, 0.9],
      },
      {
        duration,
        easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
      }
    );

    details.photoFaqAnimation.onfinish = () => {
      if (!shouldOpen) {
        details.open = false;
      }
      details.style.height = '';
      details.style.overflow = '';
      details.photoFaqAnimation = null;
    };

    details.photoFaqAnimation.oncancel = () => {
      details.style.height = '';
      details.style.overflow = '';
      details.photoFaqAnimation = null;
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initializeMotion();
      initializeFaqAccordions();
    });
  } else {
    initializeMotion();
    initializeFaqAccordions();
  }

  if (window.Shopify?.designMode) {
    document.addEventListener('shopify:section:load', (event) => {
      initializeMotion(event.target);
      initializeFaqAccordions(event.target);
    });
  }
})();
